import { useState, useEffect, useCallback, useRef } from 'react'

const POLL_INTERVAL = 45000 // 45s
const NOTIF_READ_KEY = 'hagent_notif_read_ids'

function getReadIds() {
  try {
    const raw = localStorage.getItem(NOTIF_READ_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

function saveReadIds(set) {
  localStorage.setItem(NOTIF_READ_KEY, JSON.stringify([...set]))
}

function playNotificationChime() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return
    const audioContext = new AudioContextClass()
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {})
    }

    const now = audioContext.currentTime
    // Double chime: D5 (587.33 Hz) then A5 (880 Hz)
    ;[
      { at: 0, frequency: 880 },
      { at: 0.12, frequency: 1174.66 },
    ].forEach(({ at, frequency }) => {
      const oscillator = audioContext.createOscillator()
      const gain = audioContext.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = frequency
      
      gain.gain.setValueAtTime(0.0001, now + at)
      gain.gain.exponentialRampToValueAtTime(0.06, now + at + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.15)
      
      oscillator.connect(gain)
      gain.connect(audioContext.destination)
      oscillator.start(now + at)
      oscillator.stop(now + at + 0.18)
    })
  } catch (err) {
    console.error('Lỗi phát âm thanh thông báo:', err)
  }
}

export default function useNotifications(token) {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const timerRef = useRef(null)
  const isInitialLoad = useRef(true)
  const prevUnreadIdsRef = useRef(new Set())

  useEffect(() => {
    isInitialLoad.current = true
    prevUnreadIdsRef.current = new Set()
  }, [token])

  useEffect(() => {
    window.__testNotificationChime = playNotificationChime
    window.__playNotificationChime = playNotificationChime
    return () => {
      delete window.__testNotificationChime
      delete window.__playNotificationChime
    }
  }, [])

  const fetchNotifications = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const res = await fetch('/api/notifications?hours=48&limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      setNotifications(data)

      const readIds = getReadIds()
      const currentUnread = data.filter(n => !readIds.has(n.id))
      const currentUnreadIds = new Set(currentUnread.map(n => n.id))
      setUnreadCount(currentUnread.length)

      if (!isInitialLoad.current) {
        let hasNewUnread = false
        for (const notif of currentUnread) {
          if (!prevUnreadIdsRef.current.has(notif.id)) {
            hasNewUnread = true
            break
          }
        }
        if (hasNewUnread) {
          try { if (localStorage.getItem('hagent_chat_sound_enabled') !== 'false') playNotificationChime() } catch { playNotificationChime() }
        }
      } else {
        isInitialLoad.current = false
      }

      prevUnreadIdsRef.current = currentUnreadIds
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [token])

  const markRead = useCallback((notifId) => {
    const readIds = getReadIds()
    if (readIds.has(notifId)) return
    readIds.add(notifId)
    saveReadIds(readIds)
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const markAllRead = useCallback(() => {
    const allIds = new Set(notifications.map(n => n.id))
    saveReadIds(allIds)
    setUnreadCount(0)
  }, [notifications])

  useEffect(() => {
    fetchNotifications()
    timerRef.current = setInterval(fetchNotifications, POLL_INTERVAL)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [fetchNotifications])

  return { notifications, unreadCount, loading, fetchNotifications, markRead, markAllRead }
}
